import {
    useEffect, useState, createContext,
    ReactNode, useCallback, useContext,
    useRef,
} from 'react';
import { Backdrop, CircularProgress } from '@mui/material';
import { accountService, ServerError, IApiUserSession, ApiUserPermissionResult, settingsService, SystemSettings } from '@tap/data-providers';
import { useNavigate, useSearchParams } from 'react-router';
import { ForbiddenPage } from '../pages';
import Layout from '../layout/Layout';
import { useFetchData } from '@tap/mui-ui';

/*
*   Провайдер для сессии
*/

// Задержка для интервала проверки бездействия пользователя (60000 мс = 1 минута) 
const INTERVAL_DELAY_MS = 60000;
// Время, через которое происходит выход пользователя из системы (1200000 мс = 20 минут) 
const LOGOUT_TIME_MS = 1200000;

export type AuthSession = {
    user?: IApiUserSession,
    featureList?: ApiUserPermissionResult[],
    error?: Error,
    isLoading: boolean,
    isReady: boolean,
    systemSettings: SystemSettings | undefined,
    updateActivity: (clearListen?: boolean) => void,
}

export type SessionContextInterface = {
    refreshSession: () => Promise<IApiUserSession | undefined>,
    signOut: () => void,
} & AuthSession

export const SessionContext = createContext<SessionContextInterface | null>(null);

export enum AppCode {
    Aps = 'aps',
    Mes = 'mes',
    Documents = 'documents',
    Warehouse = 'warehouse',
    Admin = 'admin',
    History = 'history',
}

export enum UserRole {
    Any = '',
    Admin = 'A',
    Dispatcher = 'D',
    Plan = 'Pl',
    Tech = 'T',
}

export type SessionProviderProps = {
    children?: ReactNode,
    authTokenKeyInCookie?: string,
    userRole: UserRole,
    appCode: AppCode,
}


export const SessionProvider = ({ children, userRole, appCode }: SessionProviderProps): JSX.Element => {
    const lastActivityTime = useRef<number | undefined>(Date.now());

    const updateActivity = (clearListen: boolean = false) => {
        lastActivityTime.current = clearListen ? undefined : Date.now();
    };

    const [user, setUser] = useState<IApiUserSession>();
    const [error, setError] = useState<ServerError>();
    const [isLoading, setIsLoading] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [_systemSettingsState, systemSettings] = useFetchData(() => settingsService.getSystemSettings(), [])

    const refreshSession = useCallback(async (): Promise<IApiUserSession | undefined> => {
        setIsLoading(true);

        const token = searchParams.get('token');
        return accountService.getSession(token, appCode)
            .then((user) => {
                if (token) {
                    searchParams.delete('token');
                    navigate({ search: searchParams.toString() }, { replace: true });
                }

                setUser(user);
                setIsReady(true);
                return user;
            })
            .catch((error: ServerError) => {
                setError(error);
                signOut();
                return undefined;
            })
            .then((finallyResult) => {
                setIsLoading(false);
                return finallyResult;
            });
    }, []);

    const signOut = useCallback(async () => {
        await accountService.signOut()
            .then(() => {
                const qp = new URLSearchParams();
                if (userRole !== UserRole.Any) {
                    qp.append('role', userRole);
                }
                qp.append('app', appCode);
                qp.append('returnUrl', window.location.href);

                window.location.href = `/auth/#/?${qp.toString()}`;
            });
    }, []);

    // Проверяем, превысил ли пользователь время бездействия в системе
    useEffect(() => {
        if (!user)
            return;

        const interval = setInterval(() => {
            const dateNow = Date.now();
            if (lastActivityTime?.current && ((dateNow - lastActivityTime.current) >= LOGOUT_TIME_MS)) {
                updateActivity(true);
                signOut();
            }
        }, INTERVAL_DELAY_MS);

        return () => clearInterval(interval);
    }, [user, refreshSession, signOut]);

    useEffect(() => {
        refreshSession();
    }, [refreshSession]);


    const isForbidden = !user?.allUserRoles.includes(userRole) && !user?.features?.filter(f => f.allow).length;

    return (
        <SessionContext.Provider
            value={{
                user,
                featureList: user?.features,
                error,
                isLoading,
                isReady,
                systemSettings,
                refreshSession,
                signOut,
                updateActivity,
            }}
        >
            {!isReady && (
                <Backdrop open={true} invisible={true}>
                    <CircularProgress color='inherit' />
                </Backdrop>
            )}
            {isReady && (
                <>
                    {isForbidden && (
                        <Layout>
                            <ForbiddenPage errorText={'У вас недостаточно прав для просмотра этой страницы'} />
                        </Layout>
                    )}
                    {!isForbidden && children}
                </>
            )}
        </SessionContext.Provider>
    );
};

export const useSession = (): SessionContextInterface => {
    const context = useContext(SessionContext);

    if (context == null) {
        throw new Error('No SessionContext provided');
    }

    return context;
};
